import React, { useState, useEffect } from 'react';
import { InputField, Form, Button, StickySaveBar } from 'components';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'data';
import { useFormChanges } from 'hooks';
import { useTranslation } from 'react-i18next';
import { editBankInfo, fetchBankInfo } from 'data/slices/profileSlice';

interface BankDataFormData {
  bankName: string;
  bic: string;
  iban: string;
}

interface FormSaveHandler {
  save: () => Promise<void>;
  hasChanges: () => boolean;
}

export const BankData = ({ user, saveRef }: { user: any; saveRef?: React.MutableRefObject<FormSaveHandler | null> }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { loading } = useSelector((state: RootState) => state.profile);

  // Local form state
  const [formData, setFormData] = useState<BankDataFormData>({
    bankName: '',
    bic: '',
    iban: '',
  });

  // Initial form state for tracking changes
  const [initialFormData, setInitialFormData] = useState<BankDataFormData>({
    bankName: '',
    bic: '',
    iban: '',
  });

  useEffect(() => {
    if (user) {
      const newData = {
        bankName: user.bankingInfo[0].BankName || '',
        bic: user.bankingInfo[0].BIC || '',
        iban: user.bankingInfo[0].IBAN || '',
      };
      setFormData(newData);
      setInitialFormData(newData);
    }
  }, [user]);

  const isFormChanged = useFormChanges(initialFormData, formData);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveBankData = async () => {
	if (!formData.iban) {
		return;
	}
    const payload = {
      bankName: formData.bankName,
      bic: formData.bic,
      iban: formData.iban,
    };

    try {
      const response = await dispatch(editBankInfo(payload)).unwrap();
      console.log(t('bankData.updateSuccess'), response);
      dispatch(fetchBankInfo()).unwrap();
      setInitialFormData({ ...formData }); // Sync initialFormData after save
    } catch (err) {
      console.error(t('bankData.updateError'), err);
    }
  };

  // Expose save handler and form change status via ref
  useEffect(() => {
    if (saveRef) {
      saveRef.current = {
        save: handleSaveBankData,
        hasChanges: () => isFormChanged,
      };
    }
  }, [saveRef, isFormChanged, handleSaveBankData]);

  return (
    <>
      <Form
        content={
          <form className="flex flex-col gap-4">
            <div className="text-[20px] font-medium">
              {t('bankData.title')}
            </div>

            <InputField
              label={t('bankData.bankName')}
              name="bankName"
              value={formData.bankName}
              onChange={handleChange}
              placeholder={t('bankData.bankNamePlaceholder')}
			  error={!formData.bankName ? t('general.required') as string : undefined}
            />

            <InputField
              label={t('bankData.bic')}
              name="bic"
              value={formData.bic}
              onChange={handleChange}
              placeholder={t('bankData.bicPlaceholder')}
			  error={!formData.bic ? t('general.required') as string : undefined}
            />

            <InputField
              label={t('bankData.iban')}
              name="iban"
              value={formData.iban}
              onChange={handleChange}
              placeholder={t('bankData.ibanPlaceholder')}
			  error={!formData.iban ? t('general.required') as string : undefined}
            />
          </form>
        }
        maxWidth={608}
      />
      <StickySaveBar>
        <Button
          onClick={handleSaveBankData}
          label={t('bankData.saveChanges')}
          loading={loading}
          disabled={loading || !isFormChanged}
          style={{ opacity: loading || !isFormChanged ? 0.5 : 1 }}
        />
      </StickySaveBar>
    </>
  );
};
